// -------------------------------------------------------------
// FULL UPDATED FILE WITH MANUAL RECONNECT + MANUAL PING + UI HANDLERS
// -------------------------------------------------------------

import { useLtpStore } from "./LTPStore/useLTPStore";
import { useOrderStore } from "./LTPStore/useOrdersStore";
import { usePositionStore } from "./LTPStore/usePositionsStore";
import useTradesStore from "./LTPStore/useTradesStore";
import useInitialDataStore from "./LTPStore/useInitialDataStore";
import useHeartbeatStore from "./LTPStore/useHeartbeatStore";
import { useOrderDebugStore } from "./LTPStore/useOrderDebugStore";
import { useWebSocketStatusStore } from "./LTPStore/useWebSocketStatusStore";
import { usePositionDebugStore } from "./LTPStore/usePositionDebugStore";
import { useRejectedOrderStore } from "./LTPStore/useRejectedOrderStore";
import { useClosedPositionStore } from "./LTPStore/useClosedPositionStore";
import { useRawPositionStore } from "./LTPStore/useRawPositionStore";
import { useExitOrdersStore } from "./LTPStore/useExitOrdersStore";
import useStraddleStore from "./LTPStore/useStraddleStore";
import useIndiaStraddleStore from "./LTPStore/useIndiaStraddleStore";
import useIndiaOtmStore from "./LTPStore/useIndiaOtmStore";
import useAlpacaStraddleStore from "./LTPStore/useAlpacaStraddleStore";
import useCashCarryArbStore from "./LTPStore/useCashCarryArbStore";
import { encryptApiKey } from "../utils/cryptoUtils";

const parseExpiryToYYMMM = (expiryStr) => {
    if (!expiryStr) return "";
    const parts = expiryStr.trim().split(/\s+/);
    if (parts.length === 3) {
        const month = parts[1].substring(0, 3).toUpperCase();
        const year = parts[2];
        const yy = year.length === 4 ? year.substring(2, 4) : year;
        return yy + month;
    }
    const match = expiryStr.match(/(\d+)([A-Z]{3})(\d{4})/i);
    if (match) {
        const yy = match[3].substring(2, 4);
        const month = match[2].toUpperCase();
        return yy + month;
    }
    return "";
};

const getSymbolFromWsMessage = (data) => {
    if (data.symbol) return data.symbol;
    if (data.exchange && data.underlying && data.expiry && data.type) {
        const yyMMM = parseExpiryToYYMMM(data.expiry);
        if (!yyMMM) return null;
        if (data.type === "CE" || data.type === "PE") {
            const strikeStr = data.strike ? Math.round(data.strike) : "";
            return `${data.exchange}:${data.underlying}${yyMMM}${strikeStr}${data.type}`;
        } else if (data.type === "FUTURE" || data.type === "FUT" || data.type === "FUTURE_FUT") {
            return `${data.exchange}:${data.underlying}${yyMMM}FUT`;
        }
    }
    return null;
};

class WebSocketManager {
    static instance = null;
    socket = null;
    pingInterval = null;

    // ------------------------------------------
    // GLOBAL LTP WEBSOCKET
    // ------------------------------------------
    globalLtpSocket = null;
    globalLtpPingInterval = null;

    // ------------------------------------------
    // UI CALLBACK HANDLERS
    // ------------------------------------------
    messageHandler = null;
    openHandler = null;
    errorHandler = null;
    closeHandler = null;

    setMessageHandler(fn) { this.messageHandler = fn; }
    setOpenHandler(fn) { this.openHandler = fn; }
    setErrorHandler(fn) { this.errorHandler = fn; }
    setCloseHandler(fn) { this.closeHandler = fn; }

    static getInstance() {
        if (!WebSocketManager.instance) {
            WebSocketManager.instance = new WebSocketManager();
        }
        return WebSocketManager.instance;
    }

    // ------------------------------------------
    // SHOULD SHOW ORDER (your logic)
    // ------------------------------------------
    shouldShowOrder(order) {
        const statusNo = Number(order.status_no);
        return statusNo === 6 || statusNo === 4;
    }

    shouldShowRejectedOrder(order) {
        const statusNo = Number(order.status_no);
        return statusNo === 5;
    }

    // ------------------------------------------
    // MANUAL RECONNECT
    // ------------------------------------------
    reconnectWebSocket() {
        try {
            console.log("🔌 Manual WebSocket reconnect requested");

            if (this.socket) {
                try {
                    this.socket.close();
                } catch (e) {
                    console.warn("⚠️ Error closing existing WS during reconnect", e);
                }
            }

            this.socket = null;
            clearInterval(this.pingInterval);

            setTimeout(() => {
                this.connect();
                this.connectGlobalLtp();
            }, 200);

        } catch (error) {
            console.error("❌ reconnectWebSocket error:", error);
        }
    }

    // ------------------------------------------
    // MANUAL PING
    // ------------------------------------------
    pingWebSocket() {
        try {
            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                console.log("📡 Sending manual ping");
                this.socket.send(JSON.stringify({ type: "ping" }));
            } else {
                console.warn("⚠️ Cannot ping — WebSocket not open");
            }
        } catch (error) {
            console.error("❌ pingWebSocket error:", error);
        }
    }
    close() {
        try {
            console.log("🔌 Closing WebSocket manually…");

            if (this.socket) {
                this.socket.close();
            }
            if (this.globalLtpSocket) {
                this.globalLtpSocket.close();
            }

            this.stopPing();
            this.stopGlobalLtpPing();
            useWebSocketStatusStore.getState().setStatus("close");

        } catch (err) {
            console.error("❌ close() error:", err);
        }
    }


    // ------------------------------------------
    // CONNECT
    // ------------------------------------------
    connect() {
        if (
            this.socket &&
            (this.socket.readyState === WebSocket.OPEN ||
                this.socket.readyState === WebSocket.CONNECTING)
        ) {
            console.log("⚠️ WS already connected");
            return;
        }

        console.log("🔌 WS Connecting...");
        useWebSocketStatusStore.getState().setStatus("connecting");

        // Generate dynamic token for authentication
        const apiKey = process.env.REACT_APP_API_KEY || "godseye_frontend_key_123";
        const secretKey = process.env.REACT_APP_SECRET_KEY || "godseye_secret_key_456";
        const token = encryptApiKey(apiKey, secretKey);

        this.socket = new WebSocket(`wss://godseye.ws.server.sypto.xyz/ws/frontend_redis?token=${token}`);


        // ------------------ OPEN ------------------
        this.socket.onopen = () => {
            console.log("🟢 WS CONNECTED");

            useWebSocketStatusStore.getState().setStatus("open");

            if (this.openHandler) {
                try { this.openHandler(); } catch (err) { console.error(err); }
            }

            this.socket.send(JSON.stringify({ type: "ping" }));
            this.startPing();
        };

        // ------------------ MESSAGE ------------------
        this.socket.onmessage = (event) => {
            let data;
            try {
                data = JSON.parse(event.data);
            } catch (err) {
                console.log("❌ WS JSON Parse Error:", err);
                return;
            }

            // 🔥 UI listener first (non-blocking)
            if (this.messageHandler) {
                try { this.messageHandler(data); } catch (err) { console.error(err); }
            }

            // Internal WS router (unchanged logic)
            this.routeMessage(data);
        };

        // ------------------ ERROR ------------------
        this.socket.onerror = (err) => {
            console.log("❌ WS ERROR:", err);
            useWebSocketStatusStore.getState().setStatus("error");

            if (this.errorHandler) {
                try { this.errorHandler(err); } catch (err2) { console.error(err2); }
            }
        };

        // ------------------ CLOSE ------------------
        this.socket.onclose = () => {
            console.log("🔴 WS CLOSED → reconnecting...");
            useWebSocketStatusStore.getState().setStatus("close");

            if (this.closeHandler) {
                try { this.closeHandler(); } catch (err) { console.error(err); }
            }

            this.stopPing();
            setTimeout(() => this.connect(), 2000);
        };
    }

    // ------------------------------------------
    // AUTO PING
    // ------------------------------------------
    startPing() {
        this.stopPing();
        this.pingInterval = setInterval(() => {
            if (this.socket?.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ type: "ping" }));
            }
        }, 10000);
    }

    stopPing() {
        if (this.pingInterval) clearInterval(this.pingInterval);
    }

    // ------------------------------------------
    // GLOBAL LTP SPECIFIC METHODS
    // ------------------------------------------
    connectGlobalLtp() {
        if (
            this.globalLtpSocket &&
            (this.globalLtpSocket.readyState === WebSocket.OPEN ||
                this.globalLtpSocket.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        console.log("🔌 Global LTP WS Connecting...");
        
        // Generate dynamic token for authentication
        const apiKey = process.env.REACT_APP_API_KEY || "godseye_frontend_key_123";
        const secretKey = process.env.REACT_APP_SECRET_KEY || "godseye_secret_key_456";
        const token = encryptApiKey(apiKey, secretKey);

        this.globalLtpSocket = new WebSocket(`wss://godseye.ws.server.sypto.xyz/ws/global_ltp?token=${token}`);


        this.globalLtpSocket.onopen = () => {
            console.log("🟢 Global LTP WS CONNECTED");
            this.globalLtpSocket.send(JSON.stringify({ type: "ping" }));
            this.startGlobalLtpPing();
        };

        this.globalLtpSocket.onmessage = (event) => {
            let rawData;
            try {
                rawData = JSON.parse(event.data);
            } catch (err) {
                console.log("❌ Global LTP WS JSON Parse Error:", err);
                return;
            }

            const processLtp = (data) => {
                // MCX Straddle snapshot — pre-computed server-side
                if (data.type === "MCX_STRADDLE_SNAPSHOT") {
                    useStraddleStore.getState().setSnapshot(data.data || []);
                    return;
                }

                // India Straddle snapshot — pre-computed server-side
                if (data.type === "INDIA_STRADDLE_SNAPSHOT") {
                    useIndiaStraddleStore.getState().setSnapshot(data.data || []);
                    return;
                }

                // India OTM snapshot — pre-computed server-side
                if (data.type === "INDIA_OTM_SNAPSHOT") {
                    useIndiaOtmStore.getState().setSnapshot(data.data || []);
                    return;
                }

                // Alpaca US Straddle snapshot — pre-computed server-side
                if (data.type === "ALPACA_STRADDLE_SNAPSHOT") {
                    useAlpacaStraddleStore.getState().setSnapshot(data.data || []);
                    return;
                }

                // Cash Carry Arb update
                if (data.tag === "CASH_CARRY_ARB") {
                    useCashCarryArbStore.getState().updateArb(data);
                    return;
                }

                const symbol = data.symbol || getSymbolFromWsMessage(data);
                if (data.ltp && symbol) {
                    useLtpStore.getState().updateLtp(symbol, data.ltp);
                    usePositionStore.getState().updatePositionLtp(symbol, data.ltp);
                }
            };

            if (Array.isArray(rawData)) {
                rawData.forEach(processLtp);
            } else {
                processLtp(rawData);
            }
        };

        this.globalLtpSocket.onerror = (err) => {
            console.log("❌ Global LTP WS ERROR:", err);
        };

        this.globalLtpSocket.onclose = () => {
            console.log("🔴 Global LTP WS CLOSED → reconnecting...");
            this.stopGlobalLtpPing();
            setTimeout(() => this.connectGlobalLtp(), 2000);
        };
    }

    startGlobalLtpPing() {
        this.stopGlobalLtpPing();
        this.globalLtpPingInterval = setInterval(() => {
            if (this.globalLtpSocket?.readyState === WebSocket.OPEN) {
                this.globalLtpSocket.send(JSON.stringify({ type: "ping" }));
            }
        }, 10000);
    }

    stopGlobalLtpPing() {
        if (this.globalLtpPingInterval) clearInterval(this.globalLtpPingInterval);
    }

    async subscribeToSymbols(symbols) {
        if (!symbols || symbols.length === 0) return;
        try {
            console.log("📡 Subscribing to global LTP symbols: ", symbols.length);
            // Assume we are using fetch since this is likely running in browser
            const apiKey = process.env.REACT_APP_API_KEY || "godseye_frontend_key_123";
            const secretKey = process.env.REACT_APP_SECRET_KEY || "godseye_secret_key_456";
            const token = encryptApiKey(apiKey, secretKey);
            
            const response = await fetch(`https://godseye.ws.server.sypto.xyz/api/ltp/subscribe?token=${token}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ symbols: symbols }),
            });

            if (!response.ok) {
                console.error("❌ Failed to subscribe to LTP symbols:", response.statusText);
            }
        } catch (error) {
            console.error("❌ Error subscribing to LTP symbols:", error);
        }
    }


    // ------------------------------------------
    // ROUTER (NO CHANGES)
    // ------------------------------------------
    routeMessage(data) {
        const { type } = data;
        // console.log("LTP UPDATE --------------",type,data);
        switch (type) {
            case "initial_data": {
                console.log("📦 Initial Data Received");

                const rawPositions = data.positions || [];
                const rawOrders = data.orders || [];
                const closedPositions = data?.closed_positions || [];
                const rawBrokerPositions = data.raw_positions || []; // 🔥 NEW

                const filteredOrders = rawOrders.filter((o) =>
                    this.shouldShowOrder(o)
                );
                const filterRejectedOrder = rawOrders.filter((o) =>
                    this.shouldShowRejectedOrder(o)
                );

                useInitialDataStore.getState().setInitialData({
                    positions: rawPositions,
                    orders: filteredOrders,
                });

                usePositionStore.getState().setInitialPositions(rawPositions);
                useOrderStore.getState().setInitialOrders(filteredOrders);
                useClosedPositionStore.getState().setInitialClosedPositions(closedPositions)
                useRejectedOrderStore.getState().setInitialOrders(filterRejectedOrder);
                useRawPositionStore.getState().setInitialRawPositions(rawBrokerPositions); // 🔥 NEW

                // -----------------------------------------
                // GLOBAL LTP SUBSCRIPTION LOGIC
                // -----------------------------------------
                const symbolSet = new Set();
                const initialLtps = {};

                const processSymbolForSubscription = (symbol, ltp = null) => {
                    if (!symbol) return;
                    let processedSymbol = symbol;
                    if (processedSymbol.startsWith("NFO:")) {
                        processedSymbol = processedSymbol.replace("NFO:", "NSE:");
                    } else if (processedSymbol.startsWith("BFO:")) {
                        processedSymbol = processedSymbol.replace("BFO:", "BSE:");
                    }
                    symbolSet.add(processedSymbol);
                    
                    if (ltp !== null && ltp !== undefined && ltp !== 0) {
                        initialLtps[processedSymbol] = ltp;
                    }
                };

                // Add symbols from open positions and seed LTPs
                rawPositions.forEach(p => {
                    if (p.symbol) {
                        const currentLtp = p.ltp || p.current_ltp;
                        processSymbolForSubscription(p.symbol, currentLtp);
                    }
                });

                // Seed the LTP store so "Live" cells have data immediately
                if (Object.keys(initialLtps).length > 0) {
                    useLtpStore.getState().setInitialLtpData(initialLtps);
                }

                // Add symbols from orders
                filteredOrders.forEach(o => {
                    if (o.symbol) processSymbolForSubscription(o.symbol);
                });

                // Add symbols from rejected orders
                filterRejectedOrder.forEach(o => {
                    if (o.symbol) processSymbolForSubscription(o.symbol);
                });

                const symbols = Array.from(symbolSet);
                this.subscribeToSymbols(symbols);
                this.connectGlobalLtp();
                // -----------------------------------------

                break;
            }

            case "ltp_update":
                // IGNORING THIS NOW, WE GET LTP FROM GLOBAL WS
                // useLtpStore.getState().updateLtp(data.symbol, data.ltp);
                // usePositionStore.getState().updatePositionLtp(data.symbol, data.ltp);
                break;

            case "order_update": {
                const order = data.order;
                if (!order) return;

                useOrderDebugStore.getState().addDebugOrder({
                    timestamp: new Date().toISOString(),
                    raw: data
                });

                const show = this.shouldShowOrder(order);
                const rejectedShow = this.shouldShowRejectedOrder(order);

                if (show) {
                    useOrderStore.getState().addOrUpdateOrder(order);
                } else {
                    useOrderStore.getState().removeOrder(order.order_id);
                }

                // 🔥 NEW: Update Exit Store for relevant statuses (2=Traded, 4=Transit, 5=Rejected, 6=Pending)
                const statusNo = Number(order.status_no);
                if ([1, 2, 4, 5, 6].includes(statusNo)) {
                    useExitOrdersStore.getState().addOrUpdateOrder(order);
                }

                if (rejectedShow) {
                    useRejectedOrderStore.getState().addOrUpdateOrder(order);
                } else {
                    useRejectedOrderStore.getState().removeOrder(order.order_id);
                }

                break;
            }

            case "order_removed":
                useOrderDebugStore.getState().addDebugOrder({
                    timestamp: new Date().toISOString(),
                    raw: data
                });
                useOrderStore.getState().removeOrder(data.order_id);
                useRejectedOrderStore.getState().removeOrder(data.order_id);
                break;

            case "position_update":
            case "new_position":
                usePositionDebugStore.getState().addDebugPosition({
                    timestamp: new Date().toISOString(),
                    raw: data,
                });
                if (data.position.net_qty === 0) {
                    useClosedPositionStore.getState().addOrUpdatePosition(data.position);
                    usePositionStore.getState().removePositionById(data.position.id);
                } else {
                    usePositionStore.getState().addOrUpdatePosition(data.position);
                }
                break;

            case "raw_position_update":  // 🔥 NEW
                useRawPositionStore.getState().addOrUpdateRawPosition(data.position || data); // data might contain 'position' key or be the position itself, backends sends wrapper usually
                break;

            case "raw_positions_snapshot": // 🔥 NEW
                useRawPositionStore.getState().setInitialRawPositions(data.raw_positions || []);
                break;

            case "position_closed":
                usePositionStore.getState().removePositionById(data.position_id);
                break;

            case "trades_update":
                useTradesStore.getState().setLatestTrades(data.trades);
                break;

            case "heartbeat":
                useHeartbeatStore.getState().updateHeartbeat(data);
                break;

            case "ping":
            case "pong":
                break;

            default:
                console.log("⚠️ Unhandled WS Type:", type, data);
        }
    }
}

export default WebSocketManager;
